/**
 * Campaigns Service
 * Handles API calls for campaigns management
 */
import { apiGet, apiPost, apiPut, apiDelete } from './api';

export interface Campaign {
  campaignID: number;
  campaignName: string;
  startDate: string;
  endDate: string;
  goalAmount?: number;
  description?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
}

/**
 * Get all active campaigns
 */
export async function getCampaigns(): Promise<Campaign[]> {
  const response = await apiGet<Campaign[]>('/campaigns');

  if (!response.success) {
    const serverMsg = response.error?.message || '';
    throw new Error(serverMsg || 'Unable to load campaigns. Please try again.');
  }

  return response.data || [];
}

/**
 * Create a new campaign
 */
export async function createCampaign(data: {
  campaignName: string;
  startDate: string;
  endDate: string;
  goalAmount?: number;
  description?: string;
}): Promise<Campaign> {
  const response = await apiPost<Campaign>('/campaigns', data);

  if (!response.success) {
    const serverMsg = response.error?.message || '';
    throw new Error(serverMsg || 'Unable to create campaign. Please try again.');
  }

  return response.data;
}

/**
 * Update a campaign
 */
export async function updateCampaign(
  id: number,
  data: {
    campaignName: string;
    startDate: string;
    endDate: string;
    goalAmount?: number;
    description?: string;
  }
): Promise<Campaign> {
  const response = await apiPut<Campaign>(`/campaigns/${id}`, data);

  if (!response.success) {
    const serverMsg = response.error?.message || '';
    throw new Error(serverMsg || 'Unable to update campaign. Please try again.');
  }

  return response.data;
}

/**
 * Delete a campaign (soft delete)
 */
export async function deleteCampaign(id: number): Promise<void> {
  const response = await apiDelete<void>(`/campaigns/${id}`);

  if (!response.success) {
    const serverMsg = response.error?.message || '';
    throw new Error(serverMsg || 'Unable to delete campaign. Please try again.');
  }
}
